import React, { useState, useEffect } from 'react';
import { Sparkles, Check, X, Camera } from 'lucide-react';

interface ImageRecognitionModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageData: string;
  onConfirm: (suggestion: any) => void;
}

const ImageRecognitionModal: React.FC<ImageRecognitionModalProps> = ({
  isOpen,
  onClose,
  imageData,
  onConfirm
}) => {
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [selectedSuggestion, setSelectedSuggestion] = useState<any>(null);

  useEffect(() => {
    if (isOpen && imageData) {
      analyzeImage();
    }
  }, [isOpen, imageData]);

  const analyzeImage = async () => {
    setIsAnalyzing(true);
    
    // Simulation de l'analyse d'image
    setTimeout(() => {
      const mockSuggestions = [
        {
          nom: 'Ballon de football',
          categorie: 'Jeux extérieurs',
          sousCategorie: 'Ballons & sports',
          confidence: 0.95,
          description: 'Ballon de football en cuir synthétique'
        },
        {
          nom: 'Ballon de basket',
          categorie: 'Jeux extérieurs',
          sousCategorie: 'Ballons & sports',
          confidence: 0.78,
          description: 'Ballon de basket-ball orange'
        },
        {
          nom: 'Microscope',
          categorie: 'Matériel électronique',
          sousCategorie: 'Instruments',
          confidence: 0.65,
          description: 'Microscope optique de laboratoire'
        }
      ];
      
      setSuggestions(mockSuggestions);
      setSelectedSuggestion(mockSuggestions[0]);
      setIsAnalyzing(false);
    }, 2000);
  };

  const handleConfirm = () => {
    if (selectedSuggestion) {
      onConfirm(selectedSuggestion);
      onClose();
    }
  };

  const handleCustomSuggestion = () => {
    const customSuggestion = {
      nom: '',
      categorie: '',
      sousCategorie: '',
      confidence: 0,
      description: 'Suggestion personnalisée'
    };
    onConfirm(customSuggestion);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <Sparkles className="h-6 w-6 mr-2 text-yellow-500" />
              Reconnaissance d'image
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Image analysée */}
          <div className="mb-6">
            <img
              src={imageData}
              alt="Image analysée"
              className="w-full h-48 object-cover rounded-lg border border-gray-200"
            />
          </div>

          {isAnalyzing ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Analyse de l'image en cours...</p>
              <p className="text-sm text-gray-500 mt-2">Recherche d'objets similaires dans la base de données</p>
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Suggestions détectées :</h3>
              
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedSuggestion === suggestion
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedSuggestion(suggestion)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{suggestion.nom}</h4>
                      <p className="text-sm text-gray-600">
                        {suggestion.categorie} - {suggestion.sousCategorie}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{suggestion.description}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="text-sm text-gray-500">
                        {Math.round(suggestion.confidence * 100)}%
                      </div>
                      {selectedSuggestion === suggestion && (
                        <Check className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Option de suggestion personnalisée */}
              <div
                className="p-4 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors"
                onClick={handleCustomSuggestion}
              >
                <div className="flex items-center">
                  <Camera className="h-5 w-5 mr-3 text-gray-400" />
                  <div>
                    <h4 className="font-medium text-gray-900">Saisie manuelle</h4>
                    <p className="text-sm text-gray-500">Je ne trouve pas l'objet dans les suggestions</p>
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleConfirm}
                  disabled={!selectedSuggestion}
                  className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Check className="h-5 w-5 mr-2" />
                  Confirmer et continuer
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageRecognitionModal;




